<template>
  <section 
    v-if="hasIngredients" 
    id="ingredients" 
    class="lg:pl-0 px-6 lg:px-[2.142857rem] lg:py-16 pb-8 lg:scroll-mt-40 scroll-mt-70"
  >
    <div class="pb-[1.0714rem] mb-6 border-b border-gray-300">
      <Heading :level="4" color="text-ash-200">Ingredient Information</Heading>
    </div>
    
    <div class="flex flex-col md:flex-row gap-5 mt-4">
      <div class="hidden md:block shrink-0 flex-none w-1/6">
         <svg class="max-h-20 max-w-20"
        aria-hidden="true" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="730" height="730" viewBox="0 0 730 730"><defs/><g transform="scale(1,1)"><g transform="scale(1,1)"><rect fill="rgb(255,255,255)" stroke="none" x="0" y="0" width="730" height="730" fill-opacity="1"/></g><path fill="none" stroke="rgb(0,0,0)" paint-order="fill stroke markers" d=" M 20 220 L 120 20 L 720 120 L 320 720 L 20 220 L 20 220" stroke-opacity="1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-width="7"/><path fill="none" stroke="rgb(0,0,0)" paint-order="fill stroke markers" d=" M 420 420 L 20 220" stroke-opacity="1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-width="7"/><path fill="none" stroke="rgb(0,0,0)" paint-order="fill stroke markers" d=" M 420 420 L 120 20" stroke-opacity="1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-width="7"/><path fill="none" stroke="rgb(0,0,0)" paint-order="fill stroke markers" d=" M 420 420 L 720 120" stroke-opacity="1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-width="7"/><path fill="none" stroke="rgb(0,0,0)" paint-order="fill stroke markers" d=" M 420 420 L 320 720" stroke-opacity="1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-width="7"/></g><g/></svg>
      </div>

      <div class="flex-1">
        <div v-if="hasKeyIngredients" class="mb-6">
          <div class="mb-6">
          <Heading :level="5" color="text-ash-200">Key Ingredients</Heading>
          </div>
          <div class="flex flex-wrap gap-2 mb-6">
            <div
              v-for="(keyIngredient) in keyIngredients"
              :key="keyIngredient.id"
              class="inline-block px-[0.643rem] py-0 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors duration-200"
            >
              <span class="text-gray-800">{{ keyIngredient.ingredient.name }}</span>
            </div>
          </div>
          <hr class="border-t border-gray-300" />
        </div>

        <Heading :level="5" color="text-ash-200" class="mb-2">Ingredient Amounts</Heading>
        <div class="mb-4 space-y-1">
          <div v-if="product.recommendedDosage" class="text-ash-100 text-base">
            Serving Size: {{ product.recommendedDosage.min.toString() + ' ' + product.recommendedDosage.unit }}
          </div>
          <div v-if="product.totalUnitCount && product.recommendedDosage" class="text-ash-100 text-base">
            Servings Per Container: {{ (product.totalUnitCount / product.recommendedDosage.min ).toString() }}
          </div>
        </div>

        <div class="relative">
          <div 
            :class="[
              'overflow-hidden transition-all duration-300'
            ]"
          >
            <div class="overflow-x-auto" ref="descriptionRef">
              <Transcript id="ingredient-info" typevalue="product" :showMoreBtn="isDisplayShowButton" :expanded="expandIngredientTranscript">
                 <template #description>
                  <table class="w-full border-collapse">
                    <caption class="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0">
                      Ingredient list
                    </caption>
                    <thead>
                      <tr class="border-b border-gray-300">
                        <th scope="col" class="text-left p-[1.0714rem] lg:py-[1.429rem] lg:px-3 font-semibold text-ash-500 text-base">
                          Name
                        </th>
                        <th scope="col" class="text-left p-[1.0714rem] lg:py-[1.429rem] lg:px-3 font-semibold text-ash-500 text-base">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr 
                        v-for="(ingredientInfo, index) in product.ingredients"
                        :key="index"
                        class="border-b border-gray-300 hover:bg-gray-50 transition-colors"
                      >
                        <template v-if="!ingredientInfo.otherIngredient">
                          <td class="py-[1.429rem] px-3 text-ash-500 text-base">{{ ingredientInfo.ingredient.name }}</td>
                          <td class="py-[1.429rem] px-3 text-ash-500 text-base">
                            {{ ingredientInfo.amountPerServing?.toString() }}{{ ingredientInfo.amountUnit }}
                          </td>
                        </template>
                      </tr>
                      <tr v-if="hasOtherIngredients" class="border-b border-gray-200">
                        <td colspan="2" class="py-[1.429rem] px-3">
                          <strong class="text-gray-800 text-base">Other Ingredients:</strong>
                          <template v-for="(ingredientInfo, index) in otherIngredients">
                            <template v-if="index != otherIngredients.length-1">
                              {{ ingredientInfo.ingredient.name + ", "}}
                            </template>
                            <template v-else>
                              {{ ingredientInfo.ingredient.name}}
                            </template>
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                 </template> 
              </Transcript>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, nextTick } from 'vue'
import Transcript from '../../molecule/transcript.vue'
import Heading from '../../atom/heading.vue'
import { useStickyTabs } from "~/composables/useNavSticky";

import type { Product } from '~~/shared/types/common'
import { URL_CONFIG } from '~~/shared/utils/url.const'

const props = defineProps<{
  product: Product
}>()

const { expandedSections } = useStickyTabs()
const expandIngredientTranscript = computed(() => {
  return expandedSections.value.ingredients
})
const descriptionRef = ref<HTMLElement | null>(null)
const sectionHeight = ref(0);

const hasIngredients = computed(() =>
  props.product.ingredients.length > 0
)

const keyIngredients = computed(() =>
  props.product.ingredients.filter(ingredientInfo => ingredientInfo.keyIngredient)
)

const hasKeyIngredients = computed(() =>
  keyIngredients.value.length > 0
)

const otherIngredients = computed(() =>
  props.product.ingredients.filter(ingredientInfo => ingredientInfo.otherIngredient)
)

const hasOtherIngredients = computed(() =>
  otherIngredients.value.length > 0
)

const isDisplayShowButton = computed(() =>
  sectionHeight.value > 124
)

onMounted(async () => {
  await nextTick()
  requestAnimationFrame(() => { 
    if(descriptionRef.value) {
      sectionHeight.value = descriptionRef.value.offsetHeight
    }
  })
});

</script>
